import { Component } from '@angular/core';
import { DocViewerStore } from '@store/legacy/doc-viewer.store';
import { BaseComponent } from '../../base.component';
import { combineLatest, distinctUntilChanged, of, takeUntil, tap, withLatestFrom } from 'rxjs';
import { LexbeDoc } from '@models/doc.model';
import { ActivatedRoute } from '@angular/router';
import { UserConfigsService } from '@services/user-configs.service';
import { UserConfigs } from '@models/user-configs.model';
import { AppStore } from '@store/legacy/app.store';
import { NotificationManager } from '@managers/notification.manager';
import { ReviewManager } from '@managers/review.manager';
import { DocumentViews } from "@store/doc-viewer/doc-viewer.state";
import { Store } from "@ngrx/store";
import { documentViewActions } from "@store/doc-viewer/document-view/document-view.actions";
import { currentDocumentView } from "@store/doc-viewer/document-view/document-view.selectors";
import { ReviewSetService } from '@services/review-set.service';
import { DocViewerParamReader } from '@utilities/doc-viewer-param-reader';
import { DocViewerActions } from "@store/doc-viewer/doc-viewer.actions";
import { redactionEditorActions } from '@store/doc-viewer/redaction-editor/redaction-editor.actions';
import { HelpService } from '@services/help.service';
import { ComparisonUtility } from '@utilities/comparison.utility';
import { BreakpointObserver, Breakpoints, BreakpointState } from '@angular/cdk/layout';

@Component({
    selector: 'lex-doc-viewer-action-bar',
    templateUrl: './doc-viewer-action-bar.component.html',
    styleUrls: ['./doc-viewer-action-bar.component.scss'],
    standalone: false
})
export class DocViewerActionBarComponent extends BaseComponent {
	public helpLink$ = of('https://sites.google.com/a/ediscoveryplatform.com/help/home/help');
	public currentDocumentSnapshot: LexbeDoc;
	public caseName: string = '';
	public selectedView$ = this.store.select(currentDocumentView());
	public mode: 'viewer' | 'review' = 'viewer';
	public panelState: 'collapsed' | 'expanded' = 'collapsed';
	public hasPdf: boolean = false;
	public hasOriginal: boolean = false;
	public hasTranslatedFile: boolean = false;
	public hasDocCompareSet: boolean = false;
	public hasProducedFile: boolean = false;
	public viewsDisplayed: ViewConfiguration[] = [];
	public viewsOverflow: ViewConfiguration[] = [];
	public title: string;
	public editTitle: boolean = false;
	private overflowViews: Array<DocumentViews> = ["text"];
	private currentViewModel$: any;
	private maxViews = 5;
	private readonly viewConfiguration: ViewConfiguration[] = [
		{ id: 'original', name: 'Native', icon: 'fa-duotone fa-file', isDisabled: true, isEverView: true, rank: 1 },
		{ id: 'pdf', name: 'PDF', icon: 'fa-duotone fa-file-pdf', isDisabled: true, isEverView: true, rank: 2 },
		{ id: 'html', name: 'HTML', icon: 'fa-duotone fa-file-code', isDisabled: false, isEverView: true, rank: 3 },
		{ id: 'redacted', name: 'Redacted', icon: 'fa-duotone fa-file-invoice', isDisabled: false, isEverView: true, rank: 4 },
		{ id: 'text', name: 'Text', icon: 'fa-duotone fa-file-lines', isDisabled: false, isEverView: false, rank: 5 },
		{ id: 'pages', name: 'Page', icon: 'fa-duotone fa-files', isDisabled: true, isEverView: false, rank: 6 },
		{ id: 'annotated', name: 'Annotated', icon: 'fa-duotone fa-file-pen', isDisabled: false, isEverView: false, rank: 7 },
		{ id: 'produced', name: 'Produced', icon: 'fa-duotone fa-file-circle-exclamation', isDisabled: true, isEverView: false, rank: 8 },
		{ id: 'translated', name: 'Translated', icon: 'fa-duotone fa-language', isDisabled: true, isEverView: false, rank: 9 },
		{ id: 'text-compare', name: 'Compare', icon: 'fa-duotone fa-file-plus-minus', isDisabled: true, isEverView: false, rank: 10 },
	];
	private displayNameMap = new Map([
		[Breakpoints.Medium, 3],
		[Breakpoints.Large, 4],
		[Breakpoints.XLarge, 5],
	]);

	constructor(
		public docViewerStore: DocViewerStore,
		public userConfigService: UserConfigsService,
		private route: ActivatedRoute,
		private appStore: AppStore,
		private reviewManager: ReviewManager,
		private notificationManager: NotificationManager,
		private store: Store,
		private reviewSetService: ReviewSetService,
		private paramReader: DocViewerParamReader,
		private helpService: HelpService,
		private breakpointObserver: BreakpointObserver
	) {
		super();

		this.helpLink$ = this.helpService.listenHelpLinkUrl().pipe(
			takeUntil(this.destroy$)
		);
		this.helpLink$.subscribe();

		if (localStorage.getItem('overflow-view') != null) {
			this.overflowViews.pop();
			this.overflowViews.push(localStorage.getItem('overflow-view') as DocumentViews);
		}

		this.currentViewModel$ = combineLatest([
			this.docViewerStore.currentDocument$,
			this.docViewerStore.simpleState$,
			this.selectedView$,
			breakpointObserver
				.observe([
					Breakpoints.Medium,
					Breakpoints.Large,
					Breakpoints.XLarge,
				])]).pipe(
					distinctUntilChanged(([prevDoc, prevState, , prevPoint], [currDoc, currState, , currPoint]) => {
						return (
							ComparisonUtility.isEqual(prevDoc, currDoc) &&
							ComparisonUtility.isEqual(prevState, currState) &&
							ComparisonUtility.isEqual(prevPoint, currPoint)
						);
					}),
					tap(([document, currentState, currentView, point]) => {
						this.currentDocumentSnapshot = document;
						this.title = this.docViewerStore.documentDataFormGroup.get('docTitle').value
						this.mode = currentState.mode;

						this.hasPdf = currentState.hasPdfRendering;
						this.hasOriginal = currentState.hasOriginalRendering;
						this.hasTranslatedFile = currentState.hasTranslation;
						this.hasDocCompareSet = currentState.compareDocumentId ? true : false;
						this.hasProducedFile = currentState.hasProduction;

						this.viewConfiguration.find(x => x.id === 'pdf').isDisabled = !this.hasPdf;
						this.viewConfiguration.find(x => x.id === 'pages').isDisabled = !this.hasPdf;
						this.viewConfiguration.find(x => x.id === 'original').isDisabled = !this.hasOriginal;
						this.viewConfiguration.find(x => x.id === 'translated').isDisabled = !this.hasTranslatedFile;
						this.viewConfiguration.find(x => x.id === 'text-compare').isDisabled = !this.hasDocCompareSet;
						this.viewConfiguration.find(x => x.id === 'produced').isDisabled = !this.hasProducedFile;

						this.maxViews = this.getMaxViewLength(point);
						this.configureViews(currentView);

						const selectedViewDisabled = this.viewConfiguration.filter(v => v.isDisabled).find(view => view.id === currentView);
						if (selectedViewDisabled && this.hasPdf) {
							this.store.dispatch(documentViewActions.viewUpdated({ selectedView: 'pdf' }));
						} else if (!this.hasPdf) {
							this.store.dispatch(documentViewActions.viewUpdated({ selectedView: 'html' }));
						}

						if (localStorage.getItem('overflow-view') != null) {
							this.overflowViews.pop();
							this.overflowViews.push(localStorage.getItem('overflow-view') as DocumentViews);
						}

					}),
					takeUntil(this.destroy$)
				).subscribe();

		this.docViewerStore.simpleState$.pipe(
			distinctUntilChanged((a, b) => a.compareDocumentId === b.compareDocumentId),
			withLatestFrom(this.selectedView$),
			tap(([currentState, currentView]) => {
				this.hasDocCompareSet = currentState.compareDocumentId ? true : false;
				if (this.hasDocCompareSet && currentView === 'text-compare') {
					this.configureViews(currentView);
				}
			}),
			takeUntil(this.destroy$)
		).subscribe();

		this.userConfigService.getUserConfigs().subscribe((userConfigs: UserConfigs) => {
			if (userConfigs?.currentCase) {
				this.caseName = userConfigs.currentCase.caseName;
			}
		});
	}

	public setView(selectedView: DocumentViews): void {
		const view = this.viewConfiguration.find(v => v.id === selectedView);
		if (!view)
			return;

		this.configureViews(selectedView);
		if (!view.isDisabled)
			this.store.dispatch(documentViewActions.viewUpdated({ selectedView }));
	}

	public togglePanel() {
		this.panelState = this.panelState === 'collapsed' ? 'expanded' : 'collapsed';
	}

	public save() {
		this.notificationManager.displaySnackBarMessage({
			message: 'Saving Changes',
			ttl: 5000
		});
		this.store.dispatch(DocViewerActions.saveRequested());
		this.reviewManager.requestSave();
	}

	public cancel(): void {
		this.reviewManager.refreshDocument(true);
		this.store.dispatch(redactionEditorActions.markEditorClean());
		this.notificationManager.displaySnackBarMessage({
			message: 'Cancelled'
		});
	}

	public close(): void {
		if (this.mode === 'review') {
			this.reviewManager.exitReview();
			return;
		}

		window.self.close();
	}

	public setTitleEdit(value: string): void {
		this.editTitle = false;
		this.title = value;
		this.docViewerStore.documentDataFormGroup.controls['docTitle'].patchValue(this.title);
		this.docViewerStore.documentDataFormGroup.controls['docTitle'].markAsDirty();
	}

	private getMaxViewLength(state: BreakpointState): number {
		let splice = 5;
		for (const query of Object.keys(state.breakpoints)) {
			if (state.breakpoints[query]) {
				splice = this.displayNameMap.get(query) ?? 5;
			}
		}
		return splice
	}

	private configureViews(selectedView: DocumentViews): void {
		const view = this.viewConfiguration.find(v => v.id === selectedView);
		let viewsToDisplay = [];
		if (!this.viewsDisplayed.find(v => v.id === view.id)) {
			viewsToDisplay = this.viewConfiguration.filter(v => v.isEverView).splice(0, this.maxViews);
			if (!viewsToDisplay.find(v => v.id === view.id))
				viewsToDisplay.push(view);

			viewsToDisplay = this.validateView(selectedView, viewsToDisplay, this.maxViews);
		}
		else {
			viewsToDisplay = this.validateView(selectedView, this.viewsDisplayed, this.maxViews);
		}

		this.viewsDisplayed = viewsToDisplay;
		this.viewsOverflow = this.viewConfiguration.filter(v => !this.viewsDisplayed.includes(v));
	}

	private validateView(view: DocumentViews, list: ViewConfiguration[], max: number) {
		while (list.length < max) {
			const nextItem = this.viewConfiguration
				.filter(v => !list.includes(v))
				.reduce((minItem, currentItem) => {
					return currentItem.rank < minItem.rank ? currentItem : minItem;
				});
			if (!list.find(v => v.id === nextItem.id))
				list.push(nextItem)
		}

		while (list.length > max) {
			const selectedViewIndex = list.findIndex(v => v.id === view);
			if (selectedViewIndex > 0 && list.length === selectedViewIndex + 1)
				list.splice(selectedViewIndex - 1, 1);
			else
				list.pop();
		}

		list.sort((a, b) => a.rank - b.rank);
		return list;
	}

}

export interface ViewConfiguration {
	id: DocumentViews,
	name: string,
	icon: string,
	isDisabled: boolean,
	isEverView: boolean,
	rank: number
}
